import { useEffect, useMemo, useState } from "react";
import { Wallet, TrendingUp, TrendingDown, Plus, Trash2, Users, Building2, Zap, Package, Loader2, DollarSign } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CATS = [
  { k: "salary", l: "Salaires", icon: Users, color: "#8B5CF6" },
  { k: "boutique", l: "Boutique Dakar", icon: Building2, color: "#00A86B" },
  { k: "fixed", l: "Charges fixes", icon: Zap, color: "#FF6B00" },
  { k: "other", l: "Autres", icon: Package, color: "#0EA5E9" },
];

function fmt(n, cur = "XOF") { return `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} ${cur}`; }
function ymNow() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; }
function formatDate(iso) { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; } }
const MONTH_LABEL = ["","Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
function readableMonth(ym) { const [y,m]=ym.split("-").map(Number); return `${MONTH_LABEL[m]||ym} ${y}`; }

export default function FinancePanel() {
  const [month, setMonth] = useState(ymNow());
  const [summary, setSummary] = useState(null);
  const [byClient, setByClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ label: "", category: "salary", amount: "", currency: "XOF", date: "", beneficiary: "", recurring: false, notes: "" });
  const [rev, setRev] = useState({ label: "", client: "", base_amount: "", rate: "", amount: "", currency: "CNY", kind: "commission", date: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get(`/admin/finance/summary?month=${month}`),
        api.get(`/admin/finance/commissions-by-client`),
      ]);
      setSummary(s.data); setByClient(c.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]);

  const addExpense = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount), date: form.date || undefined };
      await api.post("/admin/expenses", payload);
      setForm({ ...form, label: "", amount: "", beneficiary: "", notes: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const delExpense = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    await api.delete(`/admin/expenses/${id}`);
    load();
  };

  const addRevenue = async (e) => {
    e.preventDefault();
    try {
      const base = parseFloat(rev.base_amount);
      const rate = parseFloat(rev.rate);
      const amt = rev.amount ? parseFloat(rev.amount) : (base && rate ? (base * rate) / 100 : NaN);
      if (!amt || isNaN(amt)) return setErr("Renseignez soit le montant, soit (base + taux %)");
      const payload = {
        label: rev.label,
        client: rev.client || null,
        amount: amt,
        currency: rev.currency,
        kind: rev.kind,
        date: rev.date || undefined,
        rate: isNaN(rate) ? null : rate,
        base_amount: isNaN(base) ? null : base,
        notes: rev.notes || null,
      };
      await api.post("/admin/revenues", payload);
      setRev({ ...rev, label: "", client: "", base_amount: "", rate: "", amount: "", notes: "" });
      setErr("");
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const delRevenue = async (id) => {
    if (!window.confirm("Supprimer ce revenu ?")) return;
    await api.delete(`/admin/revenues/${id}`);
    load();
  };

  const months = useMemo(() => {
    const arr = []; const d = new Date();
    for (let i = 0; i < 12; i++) { arr.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`); d.setMonth(d.getMonth()-1); }
    return arr;
  }, []);

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (loading || !summary) return <div className="flex items-center gap-2 text-sm text-[var(--ink-soft)]"><Loader2 className="animate-spin" size={16}/> Chargement finance…</div>;

  const net = summary.net;

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="kicker !text-[var(--secondary)] flex items-center gap-1"><Wallet size={12}/> Finance</p>
          <h3 className="font-display text-2xl font-black mt-1">{readableMonth(month)}</h3>
        </div>
        <select value={month} onChange={(e)=>setMonth(e.target.value)} className="input-field !py-2 w-52" data-testid="finance-month">
          {months.map(m=><option key={m} value={m}>{readableMonth(m)}</option>)}
        </select>
      </div>

      {/* P&L KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-flat p-6 border-l-4 border-green-500">
          <TrendingUp className="text-green-500" size={22} />
          <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">Revenus (commissions)</p>
          <p className="font-display text-3xl font-black mt-1 text-green-600">{fmt(summary.revenue.total)}</p>
          <p className="text-[10px] text-[var(--ink-soft)] mt-1">{summary.revenue.lines.length} opérations</p>
        </div>
        <div className="card-flat p-6 border-l-4 border-orange-500">
          <TrendingDown className="text-orange-500" size={22} />
          <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">Dépenses totales</p>
          <p className="font-display text-3xl font-black mt-1 text-orange-600">{fmt(summary.expenses.total)}</p>
          <p className="text-[10px] text-[var(--ink-soft)] mt-1">{summary.expenses.list.length} lignes</p>
        </div>
        <div className={`card-flat p-6 border-l-4 ${net >= 0 ? "border-blue-500" : "border-red-500"}`}>
          <Wallet className={net >= 0 ? "text-blue-500" : "text-red-500"} size={22} />
          <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">Résultat net</p>
          <p className={`font-display text-3xl font-black mt-1 ${net >= 0 ? "text-blue-600" : "text-red-600"}`}>{fmt(net)}</p>
          <p className="text-[10px] text-[var(--ink-soft)] mt-1">{net >= 0 ? "Bénéfice ✓" : "Déficit ✗"}</p>
        </div>
      </div>

      {/* Expenses form + list */}
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={addExpense} className="card-flat p-6 space-y-3 lg:col-span-1" data-testid="expense-form">
          <h4 className="font-display text-lg font-bold flex items-center gap-2"><Plus size={16}/> Ajouter une dépense</h4>
          <div className="grid grid-cols-2 gap-2">
            {CATS.map(c=>{
              const I=c.icon;
              return (
                <button key={c.k} type="button" onClick={()=>setForm({...form,category:c.k})} className={`p-2 rounded border-2 text-xs font-bold flex flex-col items-center gap-1 transition-colors ${form.category===c.k?"border-[var(--accent)] bg-[var(--accent)]/5":"border-black/10 hover:border-black/20"}`} data-testid={`cat-${c.k}`}>
                  <I size={14} style={{color:c.color}}/> {c.l}
                </button>
              );
            })}
          </div>
          <input required placeholder="Libellé (ex: Salaire Ibrahima Mars)" value={form.label} onChange={(e)=>setForm({...form,label:e.target.value})} className="input-field" data-testid="expense-label" />
          <input placeholder="Bénéficiaire (nom employé, boutique...)" value={form.beneficiary} onChange={(e)=>setForm({...form,beneficiary:e.target.value})} className="input-field" />
          <div className="flex gap-2">
            <input required type="number" step="100" placeholder="Montant" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} className="input-field flex-1" data-testid="expense-amount" />
            <select value={form.currency} onChange={(e)=>setForm({...form,currency:e.target.value})} className="input-field w-24">
              <option value="XOF">FCFA</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="CNY">CNY</option>
            </select>
          </div>
          <input type="date" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} className="input-field" />
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={form.recurring} onChange={(e)=>setForm({...form,recurring:e.target.checked})} className="w-4 h-4 accent-[var(--accent)]"/>
            <span>Dépense récurrente (mensuelle)</span>
          </label>
          <textarea placeholder="Notes (optionnel)" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="input-field min-h-[50px]"/>
          <button type="submit" className="btn-primary w-full justify-center" data-testid="expense-submit"><Plus size={14}/> Enregistrer</button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {CATS.map(c=>{
              const I=c.icon; const v=summary.expenses.by_category[c.k]||0;
              return (
                <div key={c.k} className="card-flat p-4">
                  <I size={16} style={{color:c.color}}/>
                  <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] mt-2">{c.l}</p>
                  <p className="font-bold text-sm mt-1">{fmt(v)}</p>
                </div>
              );
            })}
          </div>
          <h4 className="font-display text-lg font-bold">Dépenses du mois ({summary.expenses.list.length})</h4>
          {summary.expenses.list.length===0 ? (
            <p className="text-sm text-[var(--ink-soft)] italic">Aucune dépense enregistrée sur ce mois.</p>
          ) : (
            <div className="space-y-2">
              {summary.expenses.list.map(e=>{
                const cat=CATS.find(c=>c.k===e.category)||CATS[3]; const I=cat.icon;
                return (
                  <div key={e.id} className="card-flat p-3 flex items-center gap-3" data-testid={`expense-${e.id}`}>
                    <I size={18} style={{color:cat.color}} className="flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm">{e.label}</p>
                      <p className="text-xs text-[var(--ink-soft)]">
                        {cat.l} · {formatDate(e.date)}
                        {e.beneficiary?` · ${e.beneficiary}`:""}
                        {e.recurring?" · 🔄 récurrent":""}
                      </p>
                    </div>
                    <p className="font-bold text-orange-600">{fmt(e.amount, e.currency)}</p>
                    <button onClick={()=>delExpense(e.id)} className="text-red-500 hover:bg-red-50 rounded p-1.5"><Trash2 size={14}/></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Revenue lines */}
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={addRevenue} className="card-flat p-6 space-y-3 lg:col-span-1" data-testid="revenue-form">
          <h4 className="font-display text-lg font-bold flex items-center gap-2 text-green-700"><DollarSign size={16}/> Ajouter une commission</h4>
          <p className="text-xs text-[var(--ink-soft)]">Ex: client caméra qui vous a payé 2% sur son achat.</p>
          <input required placeholder="Libellé (ex: Client caméra Yiwu)" value={rev.label} onChange={(e)=>setRev({...rev,label:e.target.value})} className="input-field" data-testid="revenue-label" />
          <input placeholder="Nom du client (optionnel)" value={rev.client} onChange={(e)=>setRev({...rev,client:e.target.value})} className="input-field" data-testid="revenue-client" />
          <div className="grid grid-cols-2 gap-2">
            <input type="number" step="0.01" placeholder="Montant client (base)" value={rev.base_amount} onChange={(e)=>setRev({...rev,base_amount:e.target.value})} className="input-field" data-testid="revenue-base" />
            <input type="number" step="0.01" placeholder="Taux %" value={rev.rate} onChange={(e)=>setRev({...rev,rate:e.target.value})} className="input-field" data-testid="revenue-rate" />
          </div>
          <div className="flex gap-2">
            <input type="number" step="0.01" placeholder="Ou montant direct" value={rev.amount} onChange={(e)=>setRev({...rev,amount:e.target.value})} className="input-field flex-1" data-testid="revenue-amount" />
            <select value={rev.currency} onChange={(e)=>setRev({...rev,currency:e.target.value})} className="input-field w-24" data-testid="revenue-currency">
              <option value="CNY">CNY (RMB)</option><option value="XOF">FCFA</option><option value="EUR">EUR</option><option value="USD">USD</option>
            </select>
          </div>
          {rev.base_amount && rev.rate && !rev.amount && (
            <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">= <strong>{fmt((parseFloat(rev.base_amount)*parseFloat(rev.rate))/100, rev.currency)}</strong> de commission</p>
          )}
          <select value={rev.kind} onChange={(e)=>setRev({...rev,kind:e.target.value})} className="input-field" data-testid="revenue-kind">
            <option value="commission">Commission (%)</option>
            <option value="service">Prestation / service</option>
            <option value="autre">Autre revenu</option>
          </select>
          <input type="date" value={rev.date} onChange={(e)=>setRev({...rev,date:e.target.value})} className="input-field" />
          <textarea placeholder="Notes (optionnel)" value={rev.notes} onChange={(e)=>setRev({...rev,notes:e.target.value})} className="input-field min-h-[50px]"/>
          <button type="submit" className="btn-primary w-full justify-center !bg-green-600 hover:!bg-green-700" data-testid="revenue-submit"><Plus size={14}/> Enregistrer le revenu</button>
        </form>

        <div className="lg:col-span-2">
          <h4 className="font-display text-lg font-bold mb-3">Revenus & commissions du mois</h4>
          {summary.revenue.lines.length===0 ? (
            <p className="text-sm text-[var(--ink-soft)] italic">Aucun revenu enregistré ce mois-ci. Utilisez le formulaire à gauche pour ajouter une commission (ex: 2% d'un client caméra), ou renseignez la commission dans l'onglet Expéditions.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-[var(--ink-soft)] text-left border-b border-black/10">
                    <th className="py-2">Date</th><th className="py-2">Libellé / N°</th><th className="py-2">Client</th><th className="py-2">Type</th><th className="py-2 text-right">Montant</th><th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {summary.revenue.lines.map((l,i)=>(
                    <tr key={i} className="border-b border-black/5" data-testid={`revenue-line-${i}`}>
                      <td className="py-2 text-xs">{formatDate(l.date)}</td>
                      <td className="py-2 font-mono text-xs">{l.reference}{l.source==="manual" && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-sans font-bold">manuel</span>}</td>
                      <td className="py-2 font-bold">{l.client}</td>
                      <td className="py-2 text-xs">{l.kind}</td>
                      <td className="py-2 text-right font-bold text-green-600">{fmt(l.amount, l.currency)}</td>
                      <td className="py-2 text-right">
                        {l.source==="manual" && l.id && (
                          <button onClick={()=>delRevenue(l.id)} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`revenue-del-${l.id}`}><Trash2 size={12}/></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Commission par client (tout temps) */}
      {byClient && byClient.clients.length > 0 && (
        <div>
          <h4 className="font-display text-lg font-bold mb-1">Commissions par client (depuis le début)</h4>
          <p className="text-xs text-[var(--ink-soft)] mb-3">Total tous temps : <strong className="text-green-600">{fmt(byClient.grand_total)}</strong> · {byClient.total_clients} clients rémunérateurs</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {byClient.clients.slice(0,20).map((c,i)=>(
              <div key={c.client+i} className="card-flat p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-[var(--secondary)] text-white font-bold flex items-center justify-center text-xs">
                  {c.client.trim()[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{c.client}</p>
                  <p className="text-[10px] text-[var(--ink-soft)]">{c.count} opération{c.count>1?"s":""}</p>
                </div>
                <p className="font-bold text-green-600">{fmt(c.total, c.currency)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
